import { Divider, Grid, Paper } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import { InvoiceGroups } from 'src/shared/components/form/invoiceGroups';
import SearchField from 'src/shared/components/form/searchField';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { PaymentsSearchFilter } from '../models/paymentsSearchFilter';
import { PaymentsTabItem, paymentsTabs, PaymentsTabs } from '../components/paymentsTabItem';
import { PaymentsDataGrid } from '../components/paymentsDataGrid';

export const PaymentsOverviewPage = () => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const location = useLocation();

  const [loading, setLoading] = useState<boolean>(false);
  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);

  const [paymentsSearchFilter, setPaymentsSearchFilter] = useLocalStorage(LocalStorageKeys.PaymentsFilter, {
    filter: '',
    sortCol: 'date',
    sortDir: 'desc',
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
  } as PaymentsSearchFilter);

  // When navigating with filterStatus state (e.g. from invoice dashboard), apply it
  useEffect(() => {
    const state = location.state as { filterStatus?: PaymentsTabItem } | null;
    if (state?.filterStatus) {
      setPaymentsSearchFilter((prev) => ({ ...prev, status: state.filterStatus, pageIndex: 0 }));
      window.history.replaceState({}, '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const searchFilter: PaymentsSearchFilter = useMemo(() => {
    return {
      filter: paymentsSearchFilter?.filter,
      pageIndex: paymentsSearchFilter?.pageIndex ?? DEFAULT_PAGE_INDEX,
      pageSize: paymentsSearchFilter?.pageSize ?? DEFAULT_PAGE_SIZE,
      sortCol: paymentsSearchFilter?.sortCol,
      sortDir: paymentsSearchFilter?.sortDir,
      ownerId: paymentsSearchFilter?.ownerId,
      status: paymentsSearchFilter?.status,
    } as unknown as PaymentsSearchFilter;
  }, [
    paymentsSearchFilter?.ownerId,
    paymentsSearchFilter?.pageIndex,
    paymentsSearchFilter?.pageSize,
    paymentsSearchFilter?.filter,
    paymentsSearchFilter?.sortCol,
    paymentsSearchFilter?.sortDir,
    paymentsSearchFilter?.status,
  ]);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setPaymentsSearchFilter((prev) => ({ ...prev, ...{ filter: '' } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setPaymentsSearchFilter((prev) => ({ ...prev, ...{ filter: tempSearchString } }));
      } else {
        setPaymentsSearchFilter((prev) => ({ ...prev, ...{ filter: '' } }));
      }
    }
  }, [isMounted, searchInitialized, setPaymentsSearchFilter, tempSearchString]);

  const loadingCallback = useCallback(
    (isLoading: boolean) => {
      if (isMounted()) {
        setLoading(isLoading);
      }
    },
    [isMounted],
  );

  return (
    <GridWrapper>
      <Grid item xs={12} md={4}>
        <MainContentHeader title={t('invoice:payments.header')} />
      </Grid>
      <Grid item xs={12}>
        <Paper>
          <PageFilterSection
            leftContent={
              <SearchField
                initialValue=''
                onSearch={(newSearchString) => {
                  setSearchInitialized(true);
                  setTempSearchString(newSearchString);
                }}
              />
            }
            rightContent={
              <InvoiceGroups
                callback={(ownId) => setPaymentsSearchFilter((prev) => ({ ...prev, ...{ ownerId: ownId, pageIndex: 0 } }))}
                placeholder={t('common:button.all')}
                emptyLabel={t('common:button.all')}
                autoSelect
                ownerId={paymentsSearchFilter?.ownerId}
              />
            }
          />
          <PaymentsTabs
            initialValue={searchFilter.status || paymentsTabs[0]}
            disableAll={loading}
            callback={(val) => setPaymentsSearchFilter((prev) => ({ ...prev, ...{ status: val, pageIndex: 0 } }))}
          />
          <Divider />
          <PaymentsDataGrid
            loadingCallback={loadingCallback}
            searchFilter={searchFilter}
            searchFilterCallback={(newFilter) => {
              setPaymentsSearchFilter((prev) => ({
                ...prev,
                ...newFilter,
              }));
            }}
          />
        </Paper>
      </Grid>
    </GridWrapper>
  );
};
